import { useUnmount } from "react-use";
import { useContext, useState } from "react";

import { CognitoAuthContext } from "components/Authentication/CognitoAuthContext";
import { getCaseTableMappedFilterArgs } from "./case-management/useGetCaseTableData";
import { getDDTMappedFilterArgs } from "./case-management/useGetDDTTableData";
import { dateTimeFormatISO } from "core/datetime";
import { SnackbarQueueContext } from "contexts/SnackbarQueueContext";
import { siteSettings } from "core/siteSettings";
import { downloadCasesExcel } from "core/utils/aws/downloadCasesExcel";

const getFilename = postfix => {
  const date = dateTimeFormatISO(new Date(), "YYYY-MM-DD--HH-mm-ss");
  return `${date}_${postfix}.csv`;
};

export function useExport(exportEndpointName, filterArgsMapperFunc, filename) {
  const { getToken } = useContext(CognitoAuthContext);
  const [loading, setLoading] = useState(false);

  if (!filterArgsMapperFunc) {
    filterArgsMapperFunc = () => {};
  }

  const { addSnackbarToQueue } = useContext(SnackbarQueueContext);

  const controller = new AbortController();
  useUnmount(() => controller.abort());

  const exportItems = async filters => {
    setLoading(true);

    try {
      const baseUrl = siteSettings.siteApiURL;

      const queryParams = {
        ...filterArgsMapperFunc(filters),
        _t: dateTimeFormatISO(new Date())
      };

      await downloadCasesExcel(
        `${baseUrl}/${exportEndpointName}`,
        {},
        controller.signal,
        getToken,
        queryParams,
        filename
      );
    } catch (err) {
      let message;
      if (err?.emptyBody) {
        message = "No cases available with the current combination of filters.";
      } else {
        message = "Download of export failed.";
        console.error(err ? err : message);
      }

      addSnackbarToQueue({
        message: message,
        severity: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  return [exportItems, { loading }];
}

export function useExportCases() {
  return useExport(
    "cases/export",
    getCaseTableMappedFilterArgs,
    getFilename("case_table_export")
  );
}

export function useExportDdt() {
  return useExport(
    "down_derate_data/export",
    getDDTMappedFilterArgs,
    getFilename("ddt_table_export")
  );
}
